import { FormControl, FormHelperText, FormLabel, Input, Typography } from '@mui/joy';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Nullable } from '~/common/types';

import { supportedCountryCodes, useGetPostalCodeCity } from '@/queries';
import useDebounce from '../hooks/UseDebounce';

import PostalCodeCitySelector, {
  type CitySuggestion,
} from '../components/addresses/PostalCodeCitySelector';
import FormikCountrySelector from '../components/2.0/formik/FormikCountrySelector';

const DEBOUNCE_TIMEOUT = 500;

type AddressFormikValues = {
  addressLine: Nullable<string>;
  city: Nullable<string>;
  country: Nullable<string>;
  postalCode: Nullable<string>;
};

type Props = {
  disabled?: boolean;
  required?: boolean;
};

const AddressForm: React.FC<Props> = ({ disabled = false, required = false }) => {
  const { t } = useTranslation(['common', 'errors']);
  const { values, setFieldValue, setFieldTouched, errors, touched } =
    useFormikContext<AddressFormikValues>();

  const debouncedCountry = useDebounce(values.country, DEBOUNCE_TIMEOUT);
  const debouncedCity = useDebounce(values.city, DEBOUNCE_TIMEOUT);
  const debouncedPostalCode = useDebounce(values.postalCode, DEBOUNCE_TIMEOUT);

  const addressFilled = (!!debouncedCity || !!debouncedPostalCode) && !!debouncedCountry;

  const { postalCodeCity } = useGetPostalCodeCity(
    {
      countryCode: debouncedCountry,
      term: debouncedPostalCode,
    },
    {
      enabled:
        addressFilled &&
        !!debouncedCountry &&
        supportedCountryCodes.includes(debouncedCountry.toUpperCase()),
    },
  );

  const postalCodeMismatch = useMemo(
    () =>
      addressFilled &&
      !!postalCodeCity &&
      !!postalCodeCity.length &&
      !postalCodeCity.some((city) => city.city === debouncedCity),
    [addressFilled, postalCodeCity, debouncedCity],
  );

  const handleSuggestionSelection = (suggestion: Nullable<CitySuggestion>) => {
    if (!suggestion) {
      return;
    }

    const { city, postalCode } = suggestion;

    if (city !== values.city) {
      setFieldValue('city', city);
    }
    if (postalCode !== values.postalCode) {
      setFieldValue('postalCode', postalCode);
    }
  };

  const hasError = (key: keyof typeof values) => !!touched[key] && !!errors[key];

  return (
    <>
      <FormikCountrySelector
        className="mb-3"
        disabled={disabled}
        label={t('country.label')}
        name="country"
        required={required}
      />
      <FormControl className="mb-4" error={hasError('addressLine')}>
        <FormLabel required={required}>{t('streetAndNumber')}</FormLabel>
        <Input
          disabled={disabled}
          value={values.addressLine || ''}
          onBlur={() => setFieldTouched('addressLine')}
          onChange={(e) => {
            setFieldTouched('addressLine');
            setFieldValue('addressLine', e.target.value);
          }}
        />
        {hasError('addressLine') && <FormHelperText>{errors.addressLine}</FormHelperText>}
      </FormControl>
      <div className="flex flex-row gap-4">
        <FormControl className="mb-4 w-1/3" disabled={disabled} error={hasError('postalCode')}>
          <FormLabel required={required}>{t('postalCode')}</FormLabel>
          <PostalCodeCitySelector
            city={values.city}
            country={values.country}
            disabled={disabled}
            type="postalCode"
            value={values.postalCode}
            onBlur={() => setFieldTouched('postalCode')}
            onChange={handleSuggestionSelection}
            onInputChange={(value) => {
              if (value !== values.postalCode) {
                setFieldTouched('postalCode');
                setFieldValue('postalCode', value);
              }
            }}
          />
          {hasError('postalCode') && (
            <FormHelperText className="px-1 text-xs">{errors.postalCode}</FormHelperText>
          )}
        </FormControl>
        <FormControl className="mb-4 w-2/3" disabled={disabled} error={hasError('city')}>
          <FormLabel required={required}>{t('city')}</FormLabel>
          <PostalCodeCitySelector
            country={values.country}
            disabled={disabled}
            postalCode={values.postalCode}
            required={required}
            type="city"
            value={values.city}
            onBlur={() => setFieldTouched('city')}
            onChange={handleSuggestionSelection}
            onInputChange={(value) => {
              if (value !== values.city) {
                setFieldTouched('city');
                setFieldValue('city', value);
              }
            }}
          />
          {hasError('city') && (
            <FormHelperText className="px-1 text-xs">{errors.city}</FormHelperText>
          )}
        </FormControl>
      </div>
      {postalCodeMismatch && (
        <Typography className="px-1 text-xs" color="warning">
          {t('errors:postalCodeAndCityMismatch')}
        </Typography>
      )}
    </>
  );
};

export default AddressForm;
